import React, { useMemo, useRef, useState } from "react";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import styles from "./gateway-name-dropdown.module.scss";
import clearIcon from "@src/assets/svg/close-icon.svg";
import searchIcon from "@src/assets/svg/search.svg";
import { GatewayWithMachinesViewStateTypes } from "@src/types/gateways-with-machines";

const cssClassName = "gateway-name-dropdown";

interface GatewayNameDropdownProps {
  options: GatewayWithMachinesViewStateTypes[];
  selectedGateway: GatewayWithMachinesViewStateTypes | null;
  onSelectGateway: (gateway: GatewayWithMachinesViewStateTypes | null) => void;
  gatewayLabelText: string;
  noGatewaysText: string;
  placeholderText: string;
  disabled?: boolean;
}

export const GatewayNameDropdown = ({
  options,
  selectedGateway,
  onSelectGateway,
  gatewayLabelText,
  noGatewaysText,
  placeholderText,
  disabled = false,
}: GatewayNameDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const closeDropdown = () => {
    setIsOpen(false);
    setInputValue("");
  };

  useClickOutsideListener(containerRef, closeDropdown, isOpen);

  const filteredOptions = useMemo(() => {
    const normalized = inputValue.trim().toLowerCase();
    if (!normalized) {
      return options;
    }

    return options.filter((option) =>
      (option.name || "").toLowerCase().includes(normalized),
    );
  }, [inputValue, options]);

  const handleInputFocus = () => {
    if (!disabled) setIsOpen(true);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setIsOpen(true);
  };

  const handleGatewaySelect = (gateway: GatewayWithMachinesViewStateTypes) => {
    onSelectGateway(gateway);
    setInputValue("");
    setIsOpen(false);
  };

  const handleRemoveGateway = () => {
    onSelectGateway(null);
    setInputValue("");
    setIsOpen(true);
  };

  const renderDropdownContent = () => {
    if (filteredOptions.length === 0) {
      return (
        <div className={styles[`${cssClassName}__item`]}>{noGatewaysText}</div>
      );
    }

    return filteredOptions.map((gateway) => (
      <button
        key={gateway.gateway_uuid}
        type="button"
        className={styles[`${cssClassName}__item`]}
        onClick={() => handleGatewaySelect(gateway)}
      >
        {gateway.name}
      </button>
    ));
  };

  const renderSelectedGateway = () => (
    <span className={styles[`${cssClassName}__tag`]}>
      {selectedGateway?.name}
      {!disabled && (
        <button
          type="button"
          className={styles[`${cssClassName}__tag-remove`]}
          onClick={handleRemoveGateway}
        >
          <img
            src={clearIcon}
            alt="Remove"
            className={styles[`${cssClassName}__tag-remove-icon`]}
          />
        </button>
      )}
    </span>
  );

  const renderInput = () => (
    <>
      <img
        src={searchIcon}
        alt=""
        className={styles[`${cssClassName}__search-icon`]}
      />
      <input
        className={styles[`${cssClassName}__input`]}
        placeholder={placeholderText}
        value={inputValue}
        onFocus={handleInputFocus}
        onChange={(e) => handleInputChange(e.target.value)}
      />
    </>
  );

  return (
    <div className={styles[cssClassName]}>
      <label className={styles[`${cssClassName}__label`]}>
        {gatewayLabelText}
      </label>

      <div className={styles[`${cssClassName}__input-wrapper`]}>
        {selectedGateway ? renderSelectedGateway() : renderInput()}
      </div>

      {isOpen && (
        <div ref={containerRef} className={styles[`${cssClassName}__list`]}>
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
};
